/**
 * rb-cover-letter.js — the cover letter as a page and as files.
 *
 * The letter lives in model.letter ({subject, greeting, body, closing}) — the
 * assistant's draft or the template below, then whatever the person edits. This
 * module only lays it out in the resume's colours (so the pair looks like a set)
 * and exports it: full-design PDF via the print path, real .docx.
 *
 * The template (no AI) is written gender-neutral: the old one said «Я хотел бы…
 * я уверен» — masculine Russian — to every woman who used it (2026-10-09).
 *
 * Depends on: RbI18n, RbState, RbPrint, RbExportText. globalThis.RbCoverLetter
 */
(function () {
    'use strict';

    var T = function (k, v) { return globalThis.RbI18n.t(k, v); };
    var D = function (k, m, v) { return globalThis.RbI18n.docT(k, m, v); };

    function esc(s) {
        return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    function paras(text) {
        return String(text || '').split(/\n\s*\n|\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    }

    /* A neutral first draft from the resume alone (used when the assistant is
       off or out of tries). Every sentence is a fact the person entered. */
    function draft(model) {
        var p = model.personal || {}, tg = model.target || {};
        var company = tg.company || D('cl_company_ph', model);
        var position = tg.position || p.title || D('cl_position_ph', model);
        var exp = (model.experience || [])[0];
        var body = [D('cl_opening', model, { position: position, company: company })];
        if (exp && (exp.position || exp.company)) {
            body.push(D('cl_experience', model, { position: exp.position || '', company: exp.company || '' }));
        }
        var skills = (model.skills || []).slice(0, 5).map(function (k) { return k.name; }).filter(Boolean);
        if (skills.length) body.push(D('cl_skills_line', model, { skills: skills.join(', ') }));
        body.push(D('cl_closing', model, { company: company }));
        return {
            subject: D('cl_subject', model, { position: position, name: p.name || '' }),
            greeting: tg.manager ? D('cl_greeting_named', model, { name: tg.manager }) : D('cl_greeting', model),
            body: body.join('\n\n'),
            closing: D('cl_signoff', model)
        };
    }

    /* From the assistant's answer ({subject, greeting, paragraphs[], closing}). */
    function fromAi(res) {
        return {
            subject: res.subject || '', greeting: res.greeting || '',
            body: (res.paragraphs || []).join('\n\n'), closing: res.closing || ''
        };
    }

    function letterHtml(model, opts) {
        opts = opts || {};
        var L = model.letter || {};
        var c = globalThis.RbI18n.getScheme(model.meta.scheme);
        var tpl = globalThis.RbTemplates.get(model.meta.template);
        var lang = globalThis.RbI18n.docLang(model);
        var fontStack = globalThis.RbFonts.screenStack(tpl.font, lang);
        var rtl = !!opts.rtl;
        var pg = globalThis.RbState.pageSize(model.meta && model.meta.pageSize);
        var p = model.personal || {}, tg = model.target || {};
        var today;
        try { today = new Date().toLocaleDateString(lang, { year: 'numeric', month: 'long', day: 'numeric' }); } catch (e) { today = ''; }
        var contact = [p.email, p.phone, p.city].filter(Boolean).map(esc).join(' · ');
        var mark = opts.marks ? function (h) { return h.replace(/\[([^\]<>\n]{1,80})\]/g, '<mark style="background:#fde68a;color:#5b4300;border-radius:2px;padding:0 2px">[$1]</mark>'); } : function (h) { return h; };

        var head = '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:28px;padding-bottom:14px;border-bottom:2px solid ' + c.border + '">' +
            '<div><div style="font-size:21px;font-weight:700;color:' + c.heading + '">' + esc(p.name || D('your_name_ph', model)) + '</div>' +
            (p.title ? '<div style="font-size:13px;color:' + c.accent + ';margin-top:2px">' + esc(p.title) + '</div>' : '') +
            (contact ? '<div style="font-size:12px;color:' + c.muted + ';margin-top:6px">' + contact + '</div>' : '') + '</div>' +
            '<div style="font-size:12px;color:' + c.muted + ';white-space:nowrap">' + esc(today) + '</div></div>';
        var recipient = tg.company ? '<div style="margin-bottom:18px;font-size:13px;color:' + c.text + '"><strong>' + esc(tg.company) + '</strong>' +
            (tg.position ? '<br><span style="color:' + c.muted + '">' + esc(tg.position) + '</span>' : '') + '</div>' : '';
        var body = (L.greeting ? '<p style="margin:0 0 14px">' + esc(L.greeting) + '</p>' : '') +
            paras(L.body).map(function (x) { return '<p style="margin:0 0 12px;line-height:1.65">' + esc(x) + '</p>'; }).join('') +
            (L.closing ? '<p style="margin:22px 0 2px">' + esc(L.closing) + '</p>' : '') +
            '<p style="margin:0;font-weight:700;color:' + c.heading + '">' + esc(p.name || '') + '</p>';
        if (!String(L.body || '').trim()) {
            body = '<p style="margin:40px 0;color:' + c.muted + ';text-align:center">' + esc(T('cl_empty_page')) + '</p>';
        }
        return '<div class="rb-doc" lang="' + esc(lang) + '" dir="' + (rtl ? 'rtl' : 'ltr') + '" style="width:' + pg.w +
            'px;min-height:' + pg.h + 'px;background:#fff;color:' + c.text + ';font-family:' + fontStack +
            ';font-size:13.5px;padding:64px 72px;box-sizing:border-box;' + (rtl ? 'direction:rtl;text-align:right;' : '') + '">' +
            head + recipient + mark(body) + '</div>';
    }

    function plainText(model) {
        var L = model.letter || {};
        return [L.greeting, L.body, '', L.closing, (model.personal || {}).name].filter(function (x) { return x != null; }).join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
    }

    function fileBase(model) {
        return globalThis.RbExportText.safeName(model, 'x').replace(/\.x$/, '') + '_' + T('cl_file_suffix');
    }

    function exportPdf(model, opts) {
        opts = opts || {};
        return globalThis.RbPrint.printHtml(letterHtml(model, { rtl: opts.rtl }), {
            rtl: !!opts.rtl, locale: globalThis.RbI18n.docLang(model),
            pageSize: model.meta && model.meta.pageSize,
            title: fileBase(model)
        });
    }

    function exportDocx(model, opts) {
        opts = opts || {};
        return globalThis.RbExportText.loadVendor('docx').then(function () {
            var d = globalThis.docx;
            var rtl = !!opts.rtl;
            var L = model.letter || {}, p = model.personal || {};
            var kids = [new d.Paragraph({ children: [new d.TextRun({ text: p.name || '', bold: true, size: 30 })], bidirectional: rtl })];
            if (p.title) kids.push(new d.Paragraph({ children: [new d.TextRun({ text: p.title, size: 22 })], bidirectional: rtl }));
            var contact = [p.email, p.phone, p.city].filter(Boolean).join(' · ');
            if (contact) kids.push(new d.Paragraph({ children: [new d.TextRun({ text: contact, size: 20 })], spacing: { after: 360 }, bidirectional: rtl }));
            if (L.greeting) kids.push(new d.Paragraph({ children: [new d.TextRun({ text: L.greeting, size: 22 })], spacing: { after: 200 }, bidirectional: rtl }));
            paras(L.body).forEach(function (x) {
                kids.push(new d.Paragraph({ children: [new d.TextRun({ text: x, size: 22 })], spacing: { after: 180 }, bidirectional: rtl }));
            });
            if (L.closing) kids.push(new d.Paragraph({ children: [new d.TextRun({ text: L.closing, size: 22 })], spacing: { before: 240 }, bidirectional: rtl }));
            kids.push(new d.Paragraph({ children: [new d.TextRun({ text: p.name || '', bold: true, size: 22 })], bidirectional: rtl }));
            var doc = new d.Document({ creator: 'Timbrica', sections: [{ children: kids }] });
            return d.Packer.toBlob(doc);
        }).then(function (blob) {
            return globalThis.RbExportText.download(blob, fileBase(model) + '.docx');
        });
    }

    globalThis.RbCoverLetter = {
        draft: draft, fromAi: fromAi, letterHtml: letterHtml, plainText: plainText,
        exportPdf: exportPdf, exportDocx: exportDocx
    };
})();
